import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchCollegeTeams,
  CollegeTeam,
  SortColumn,
  SortDirection,
} from "@/services/adminCollegeTeams";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const PAGE_SIZE = 50;

const AdminCollegeTeams = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState<CollegeTeam[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [season, setSeason] = useState("2025_fall");
  const [gender, setGender] = useState<"male" | "female" | "all">("all");
  const [sortBy, setSortBy] = useState<SortColumn>("national_rank");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [offset, setOffset] = useState(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchCollegeTeams({
        query: debouncedSearch || undefined,
        season,
        gender: gender === "all" ? undefined : gender,
        sortBy,
        sortDir,
        limit: PAGE_SIZE,
        offset,
      });
      setTeams(response.items);
      setTotal(response.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Failed to load college teams",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, season, gender, sortBy, sortDir, offset, toast]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setOffset(0);
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="ml-1 h-4 w-4 inline opacity-50" />;
    }
    return sortDir === "asc" ? (
      <ChevronUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4 inline" />
    );
  };

  const SortableHeader = ({
    column,
    children,
  }: {
    column: SortColumn;
    children: React.ReactNode;
  }) => (
    <TableHead
      className="cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => handleSort(column)}
    >
      {children}
      <SortIcon column={column} />
    </TableHead>
  );

  const formatGender = (g: string) => {
    return g === "male" ? "Men" : g === "female" ? "Women" : g;
  };

  const formatRank = (rank: number | null) => {
    if (rank === null || rank === undefined || rank === 0) return "-";
    return `#${rank}`;
  };

  const formatScoringAvg = (avg: number | null) => {
    if (avg === null || avg === undefined) return "-";
    return avg.toFixed(2);
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>College Teams</CardTitle>
              <CardDescription>
                View and search college golf teams with ranking data
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/recruit-admin/dashboard")}
            >
              Back to Dashboard
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Search by college name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="w-36">
              <Select
                value={gender}
                onValueChange={value => {
                  setGender(value as "male" | "female" | "all");
                  setOffset(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All genders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="male">Men</SelectItem>
                  <SelectItem value="female">Women</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Select
                value={season}
                onValueChange={value => {
                  setSeason(value);
                  setOffset(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select season" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025_fall">Fall 2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading college teams...</div>
          ) : teams.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {debouncedSearch ? `No teams found matching "${debouncedSearch}"` : "No teams found"}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader column="college_name">College Name</SortableHeader>
                      <SortableHeader column="team_name">Team Name</SortableHeader>
                      <SortableHeader column="gender">Gender</SortableHeader>
                      <SortableHeader column="division">Division</SortableHeader>
                      <SortableHeader column="state">State</SortableHeader>
                      <SortableHeader column="national_rank">
                        <span className="whitespace-nowrap">National Rank</span>
                      </SortableHeader>
                      <SortableHeader column="scoring_avg_adj">
                        <span className="whitespace-nowrap">Scoring Avg</span>
                      </SortableHeader>
                      <SortableHeader column="roster_size">
                        <span className="whitespace-nowrap">Roster Size</span>
                      </SortableHeader>
                      <SortableHeader column="head_coach">Head Coach</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teams.map(team => (
                      <TableRow
                        key={team.team_id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/recruit-admin/college-teams/${team.team_id}`)}
                      >
                        <TableCell className="font-medium">{team.college_name}</TableCell>
                        <TableCell>{team.team_name}</TableCell>
                        <TableCell>{formatGender(team.gender)}</TableCell>
                        <TableCell>{team.division || "-"}</TableCell>
                        <TableCell>{team.state || "-"}</TableCell>
                        <TableCell className="text-right">
                          {formatRank(team.national_rank)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatScoringAvg(team.scoring_avg_adj)}
                        </TableCell>
                        <TableCell className="text-right">{team.roster_size ?? "-"}</TableCell>
                        <TableCell>{team.head_coach || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total} teams
                  {debouncedSearch && ` matching "${debouncedSearch}"`}
                  {gender !== "all" && ` (${formatGender(gender)})`}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCollegeTeams;
